import React, { useEffect, useState, useCallback } from "react";
import { useCompany } from "../contexts/CompanyContext";
import { supabase } from "../lib/supabase_client";
import { useSession } from "../contexts/SessionContext";
import { generateSSTInvoice, generateOldInvoice, generateRegularInvoice, generateSingaporeInvoice, generateEInvoiceFromPayload, InvoiceTransactionData, InvoiceCustomerData } from "../services/invoiceService";
import EInvoiceDetailsModal from "./EInvoiceDetailsModal";
import '../styles/page-base.css';


const getStatusColor = (status: string) => {
    switch (status) {
        case "paid":
          return "bg-green-100 text-green-700";
        case "pending":
          return "bg-yellow-100 text-yellow-700";
        case "cancelled":
          return "bg-gray-200 text-gray-700";
        case "failed":
          return "bg-red-100 text-red-700";
        default:
          return "bg-gray-100 text-gray-700";
      }
};

type LhdnStatus = "submitted" | "valid" | "invalid" | "cancelled" | "rejected" | null;

interface SalesDoc {
    id: string;
    date: string;
    company_name: string;
    org_id: string;
    product_name: string; 
    product_id: string;
    payment_reference: string;
    amount: number;
    credits: number;
    referral_code: string; 
    status: "completed" | "pending" | "cancelled";
    product_country: string;
    currency: string;
    created_at: string;
    updated_at: string;
    sequential_invoice_number?: number;
    invoice_version?: number;
    lhdn_status: LhdnStatus;
    lhdn_submission_uid?: string | null;
    lhdn_document_uuid?: string | null;
    lhdn_long_id?: string | null;
    lhdn_consolidated_batch_id?: string | null;
}

const getLhdnStatusStyle = (doc: Pick<SalesDoc, 'lhdn_status' | 'lhdn_consolidated_batch_id'>) => {
    // Consolidated invoices get a distinct purple badge — no individual actions
    if (doc.lhdn_consolidated_batch_id) {
        return { cls: "bg-purple-100 text-purple-700", label: "Consolidated", isConsolidated: true };
    }
    switch (doc.lhdn_status) {
        case "valid":     return { cls: "bg-green-100 text-green-700",    label: "e-Invoice Valid",        isConsolidated: false };
        case "submitted": return { cls: "bg-blue-100 text-blue-700",     label: "Pending Validation",     isConsolidated: false };
        case "invalid":   return { cls: "bg-red-100 text-red-700",       label: "e-Invoice Invalid",      isConsolidated: false };
        case "cancelled": return { cls: "bg-gray-200 text-gray-500",     label: "Cancelled",              isConsolidated: false };
        case "rejected":  return { cls: "bg-orange-100 text-orange-700", label: "Rejected",               isConsolidated: false };
        default:          return null;
    }
};

export default function BillingHistory() {
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { permissions, isAdmin } = useSession();
    const [salesData, setSalesData] = useState<SalesDoc[]>([]);
    const [salesLoading, setSalesLoading] = useState(false);
    const [orgCountryIso, setOrgCountryIso] = useState<string | null>(null);
    const [showEInvoiceModal, setShowEInvoiceModal] = useState(false);
    const [pendingEInvoiceDoc, setPendingEInvoiceDoc] = useState<SalesDoc | null>(null);
    const [hasEInvoiceDetails, setHasEInvoiceDetails] = useState<boolean | null>(null);
    const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const { selectedCompany } = useCompany();
    const { 
        loading: roleLoading, 
        isUserAdmin
    } = useSession();

    const fetchSalesData = useCallback(async () => {
        if (!selectedCompany || !isUserAdmin) {
            setSalesData([]);
            return;
        }

        try {
            setSalesLoading(true);
            console.log('UserSales: Fetching sales data for company:', selectedCompany.company_id);
            
            // Fetch purchases for the current organization only
            const { data: purchases, error: purchasesError } = await supabase
                .from('org_purchases')
                .select(`
                    id,
                    amount,
                    status,
                    created_at,
                    updated_at,
                    org_id,
                    product_id,
                    payment_reference,
                    sequential_invoice_number,
                    invoice_version,
                    lhdn_status,
                    lhdn_submission_uid,
                    lhdn_document_uuid,
                    lhdn_long_id,
                    lhdn_consolidated_batch_id
                `)
                .eq('org_id', selectedCompany.company_id)
                .eq('status', 'paid')
                .order('created_at', { ascending: false });

            if (purchasesError) {
                console.error('UserSales: Error fetching purchases:', purchasesError);
                setSalesData([]);
                return;
            }

            if (!purchases || purchases.length === 0) {
                setSalesData([]);
                return;
            }

            // Get unique product IDs
            const productIds = Array.from(new Set(purchases.map(p => p.product_id)));

            // Fetch products
            const { data: products } = await supabase
                .from('products')
                .select('id, name, credit_amount, country_iso, currency')
                .in('id', productIds);

            // Create lookup map
            const productMap = new Map(products?.map(product => [product.id, product]) || []);

            // Transform the data
            const transformedData = purchases.map(item => ({
                id: item.id,
                date: new Date(item.created_at).toLocaleString(),
                company_name: selectedCompany.company_name,
                org_id: item.org_id,
                product_name: productMap.get(item.product_id)?.name || '-',
                amount: item.amount,
                product_id: item.product_id,
                credits: productMap.get(item.product_id)?.credit_amount || 0,
                referral_code: '-',
                payment_reference: item.payment_reference,
                status: item.status,
                currency: productMap.get(item.product_id)?.currency || 'MYR',
                created_at: item.created_at,
                updated_at: item.updated_at,
                product_country: productMap.get(item.product_id)?.country_iso || '',
                sequential_invoice_number: item.sequential_invoice_number,
                invoice_version: item.invoice_version,
                lhdn_status: item.lhdn_status ?? null,
                lhdn_submission_uid: item.lhdn_submission_uid ?? null,
                lhdn_document_uuid: item.lhdn_document_uuid ?? null,
                lhdn_long_id: item.lhdn_long_id ?? null,
                lhdn_consolidated_batch_id: item.lhdn_consolidated_batch_id ?? null,
            }));

            setSalesData(transformedData);
            console.log('UserSales: Sales data fetched successfully:', transformedData.length, 'records');
        } catch (error) {
            console.error('UserSales: Error fetching sales data:', error);
            setSalesData([]);
        } finally {
            setSalesLoading(false);
        }
    }, [selectedCompany?.company_id, isUserAdmin]);

    useEffect(() => {
        if (isUserAdmin && selectedCompany) {
            fetchSalesData();
        }
    }, [location.pathname, isUserAdmin, selectedCompany?.company_id, fetchSalesData]);

    useEffect(() => {
        const fetchOrgCountry = async () => {
          if (!selectedCompany?.company_id) {
            setOrgCountryIso(null);
            setHasEInvoiceDetails(null);
            return;
          }
          const [{ data: orgData, error }, { data: einvoiceRow }] = await Promise.all([
            supabase.from("organizations").select("country_iso").eq("id", selectedCompany.company_id).maybeSingle(),
            supabase.from("org_einvoice_details").select("id").eq("org_id", selectedCompany.company_id).maybeSingle(),
          ]);

          if (error) {
            console.error("BillingHistory: Error fetching org country_iso:", error);
            setOrgCountryIso(null);
            return;
          }
          setOrgCountryIso(orgData?.country_iso ?? null);
          setHasEInvoiceDetails(!!einvoiceRow);
        };
    
        fetchOrgCountry();
      }, [selectedCompany?.company_id]);

    const filteredDocuments = salesData.filter(
        (doc) => {
            const matchesSearch = (doc.company_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (doc.product_name || '').includes(searchTerm) ||
                (doc.payment_reference?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            
            return matchesSearch;
        }
    );

    // Shared function to generate invoice PDF blob
    const generateInvoice = async (doc: SalesDoc): Promise<Blob> => {
        // Fetch customer data from database
        let customerData: InvoiceCustomerData = {
            company_name: (doc.company_name || 'Unknown Company'),
            billing_address: '',
            contact_phone: undefined,
            contact_email: undefined
        };

        // Fetch organization details (address)
        const { data: orgData } = await supabase
            .from('organizations')
            .select('address')
            .eq('id', doc.org_id)
            .maybeSingle();

        if (orgData?.address) {
            customerData.billing_address = orgData.address;
        }

        // Fetch organization owner (role_id = 1 is owner)
        const { data: ownerMember } = await supabase
            .from('organization_members')
            .select('user_id')
            .eq('org_id', doc.org_id)
            .eq('role_id', 1)
            .eq('is_active', true)
            .limit(1)
            .maybeSingle();

        if (ownerMember?.user_id) {
            // Get owner email from auth.users via RPC
            const { data: userDetails } = await supabase
                .rpc('get_user_details', { user_ids: [ownerMember.user_id] });

            if (userDetails && userDetails.length > 0) {
                customerData.contact_email = userDetails[0].email || undefined;
                customerData.contact_phone = userDetails[0].contact_number || undefined;
            }
        }

        // Convert transaction data to match invoice service structure
        const transactionData: InvoiceTransactionData = {
            id: doc.id,
            created_at: doc.created_at,
            amount: doc.amount,
            credits: doc.credits,
            products: {
                name: doc.product_name
            },
            sequential_invoice_number: doc.sequential_invoice_number
        };

        // Generate invoice blob based on invoice_version
        const invoiceVersion = doc.invoice_version; 
        let pdfBlob: Blob;
        
        if (invoiceVersion === 3) {
            pdfBlob = await generateSSTInvoice(transactionData, customerData);
        } else if (invoiceVersion === 4) {
            pdfBlob = await generateSingaporeInvoice(transactionData, customerData);
        } else if (invoiceVersion === 2) {
            pdfBlob = await generateRegularInvoice(transactionData, customerData);
        } else {
            pdfBlob = await generateOldInvoice(transactionData, customerData);
        }

        return pdfBlob;
    };

    const handleDownloadInvoice = async (doc: SalesDoc) => {
        try {
            // If a valid LHDN e-invoice exists, generate PDF from the stored payload
            if (doc.lhdn_status === 'valid' && doc.lhdn_document_uuid && doc.lhdn_long_id) {
                // Fetch payload lazily — not loaded in the list query to keep it light
                const { data: purchaseRow } = await supabase
                    .from('org_purchases')
                    .select('lhdn_payload, lhdn_submitted_at')
                    .eq('id', doc.id)
                    .single();

                if (purchaseRow?.lhdn_payload) {
                    const pdfBlob = await generateEInvoiceFromPayload(
                        purchaseRow.lhdn_payload as Record<string, unknown>,
                        doc.lhdn_document_uuid,
                        doc.lhdn_long_id,
                        purchaseRow.lhdn_submitted_at
                    );
                    const seqNum = doc.sequential_invoice_number;
                    const filename = seqNum
                        ? `EINV${seqNum.toString().padStart(6, '0')}.pdf`
                        : `EINV-${doc.id.slice(0, 8)}.pdf`;
                    const url = URL.createObjectURL(pdfBlob);
                    const a = document.createElement('a');
                    a.href = url; a.download = filename; a.click();
                    URL.revokeObjectURL(url);
                    return;
                }
            }

            const pdfBlob = await generateInvoice(doc);
            
            const pdfUrl = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = pdfUrl;
            
            const invoiceVersion = doc.invoice_version;
            let filename: string;
            if (invoiceVersion === 3) {
                const sequentialNumber = doc.sequential_invoice_number;
                filename = `INV${sequentialNumber?.toString().padStart(6, '0')}.pdf`;
            } else {
                let invoiceId = doc.id;
                if (invoiceId.includes('-')) {
                    invoiceId = invoiceId.split('-')[0];
                } else {
                    invoiceId = invoiceId.substring(0, 8);
                }
                filename = `invoice_${invoiceId}.pdf`;
            }
            
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(pdfUrl);

        } catch (error) {
            console.error('Error generating invoice:', error);
            alert('Error generating invoice. Please try again.');
        }
    };

    const handleApplyEInvoice = (doc: SalesDoc) => {
        setPendingEInvoiceDoc(doc);
        setShowEInvoiceModal(true);
    };

    const showToast = (type: 'success' | 'error', message: string) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 5000);
    };

    const handleCheckLhdnStatus = async (doc: SalesDoc) => {
        if (!doc.lhdn_submission_uid && !doc.lhdn_document_uuid) return;
        try {
            const { data, error } = await supabase.functions.invoke("check-lhdn-status", {
                body: { org_purchase_id: doc.id },
            });
            if (error) { showToast('error', "Status check failed: " + error.message); return; }
            setSalesData((prev) => prev.map((d) =>
                d.id === doc.id
                    ? { ...d, lhdn_status: data?.lhdn_status ?? d.lhdn_status, lhdn_long_id: data?.lhdn_long_id ?? d.lhdn_long_id, lhdn_document_uuid: data?.lhdn_document_uuid ?? d.lhdn_document_uuid }
                    : d
            ));
            if (data?.lhdn_status === 'valid') showToast('success', 'e-Invoice validated successfully.');
        } catch (e) {
            showToast('error', "Status check error: " + String(e));
        }
    };

    const closeEInvoiceModal = () => {
        setShowEInvoiceModal(false);
        setPendingEInvoiceDoc(null);
    };

    // Runs after buyer details are saved.
    // If opened from the "Manage" badge (no pending doc), just update state and close.
    // If opened from "Apply e-Invoice" on a row, proceed to LHDN submission.
    const handleEInvoiceSaved = async () => {
        setHasEInvoiceDetails(true);
        if (!pendingEInvoiceDoc) {
            setShowEInvoiceModal(false);
            return;
        }
        try {
            const { data: submitData, error: submitError } = await supabase.functions.invoke('submit-einvoice-to-lhdn', {
                body: { org_purchase_id: pendingEInvoiceDoc.id },
            });
            if (submitError) {
                showToast('error', 'e-Invoice submission failed. Please try again.');
                console.error('[einvoice] function error:', submitError);
                return;
            }
            const ok = (submitData as { ok?: boolean })?.ok;
            const lhdnStatus = (submitData as { lhdn_status?: string })?.lhdn_status;
            if (ok && lhdnStatus === 'valid') {
                showToast('success', 'e-Invoice validated by LHDN.');
            } else if (ok) {
                showToast('success', 'e-Invoice submitted to LHDN. Awaiting validation.');
            } else {
                const lhdnResp = (submitData as { lhdn_response?: unknown })?.lhdn_response;
                console.error('[einvoice] LHDN rejection:', lhdnResp);
                showToast('error', 'LHDN rejected the e-invoice. Please check the buyer details and try again.');
            }
            await fetchSalesData();
        } catch (err) {
            console.error('[einvoice] submission exception:', err);
            showToast('error', 'e-Invoice submission failed. Please try again.');
        } finally {
            closeEInvoiceModal();
        }
    };

    const isSg = orgCountryIso === "SG";    
    const totalPages = Math.ceil(filteredDocuments.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentDocuments = filteredDocuments.slice(startIndex, endIndex);

    // Show loading while role is being fetched
    if (roleLoading) {
        return (
            <div className="min-h-screen bg-gray-50 p-8 flex items-center justify-center">
                <div className="text-gray-500">Loading user permissions...</div>
            </div>
        );
    }

    // Show access denied if not admin
    if (!permissions['bilings']) {
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center py-12">
                        <div className="text-gray-500 text-lg">Access Denied</div>
                        <div className="text-gray-400 text-sm mt-2">
                            You don't have permission to view sales records. Only owners, admins, and managers can access this section.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-card">
            {/* Toast notification */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all
                    ${toast.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
                    {toast.type === 'success' ? (
                        <svg className="w-4 h-4 shrink-0 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    ) : (
                        <svg className="w-4 h-4 shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                    )}
                    <span>{toast.message}</span>
                    <button onClick={() => setToast(null)} className="ml-2 opacity-50 hover:opacity-100">✕</button>
                </div>
            )}
            <div className="max-w-6xl mx-auto space-y-6">
            
            <div className="page-header">
            <div className="page-header-left">
            <div className="flex items-center justify-between space-x-3 mb-4">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <input
                        type="text"
                        placeholder="Search by Product or Reference"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>

                {/* E-Invoice status + action button (MY orgs only) */}
                {orgCountryIso === 'MY' && selectedCompany?.company_id && hasEInvoiceDetails !== null && (
                    <button
                        onClick={() => { setPendingEInvoiceDoc(null); setShowEInvoiceModal(true); }}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all whitespace-nowrap ${
                            hasEInvoiceDetails
                                ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100 hover:border-green-300'
                                : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 hover:border-amber-300'
                        }`}
                    >
                        {hasEInvoiceDetails ? (
                            <>
                                <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span>E-Invoice Enabled</span>
                                <span className="text-green-400">·</span>
                                <span className="text-green-600 font-semibold">Manage</span>
                            </>
                        ) : (
                            <>
                                <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                </svg>
                                <span>E-Invoice Not Set Up</span>
                                <span className="text-amber-400">·</span>
                                <span className="text-amber-700 font-semibold">Setup Now →</span>
                            </>
                        )}
                    </button>
                )}
                </div>
            </div>
            </div>

                {/* Table */}
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
                    <table className="page-table">
                        <thead className="page-table-thead">
                            <tr>
                                <th className="page-table-th" style={{ width: '13%' }}>Date</th>
                                <th className="page-table-th" style={{ width: '22%' }}>Company</th>
                                <th className="page-table-th" style={{ width: '18%', textAlign: 'center' }}>Product</th>
                                <th className="page-table-th" style={{ width: '10%', textAlign: 'center' }}>Amount</th>
                                <th className="page-table-th" style={{ width: '8%', textAlign: 'center' }}>
                                    {isSg ? 'SGD Credits' : 'Credits'}
                                </th>
                                <th className="page-table-th" style={{ width: '9%', textAlign: 'center' }}>Status</th>
                                {orgCountryIso === 'MY' && isAdmin && (
                                    <th className="page-table-th" style={{ width: '12%', textAlign: 'center' }}>e-Invoice</th>
                                )}
                                <th className="page-table-th" style={{ width: '8%', textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>

                        <tbody className="page-table-tbody">
                            {salesLoading ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-4 text-gray-500">
                                        Loading sales data...
                                    </td>
                                </tr>
                            ) : currentDocuments.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-4 text-gray-500">
                                        No transactions found.
                                    </td>
                                </tr>
                            ) : (
                                currentDocuments.map((doc, idx) => {
                                    return (
                                    <tr
                                        key={doc.id}
                                        className={`${
                                            idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                                        } hover:bg-gray-100 transition`}
                                    >
                                        <td className="page-table-td" style={{width: '15%'}}>
                                            {(() => {
                                                const dateObj = new Date(doc.created_at);
                                                const dateStr = dateObj.toLocaleDateString('en-GB');
                                                const timeStr = dateObj.toLocaleTimeString('en-GB', { 
                                                    hour: '2-digit', 
                                                    minute: '2-digit',
                                                    hour12: true 
                                                });
                                                return (
                                                    <div className="flex flex-col">
                                                        <span>{dateStr}</span>
                                                        <span className="text-gray-500 text-xs">{timeStr}</span>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="page-table-td" style={{width: '22%'}}>{doc.company_name}</td>
                                        <td className="page-table-td" style={{ width: '18%', textAlign: 'center' }}>{doc.product_name}</td>
                                        <td className="page-table-td" style={{ width: '10%', textAlign: 'center' }}>{doc.currency} {doc.amount}</td>
                                        <td className="page-table-td" style={{ width: '8%', textAlign: 'center' }}>{doc.credits}</td>
                                        <td className="page-table-td" style={{ width: '9%', textAlign: 'center' }}>
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(doc.status)}`}>
                                                {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                                            </span>
                                        </td>
                                        {orgCountryIso === 'MY' && isAdmin && (() => {
                                            const lhdn = getLhdnStatusStyle(doc);
                                            const isConsolidated = !!doc.lhdn_consolidated_batch_id;
                                            const now = new Date();
                                            const docDate = new Date(doc.created_at);
                                            const isSameMonth = docDate.getFullYear() === now.getFullYear() && docDate.getMonth() === now.getMonth();
                                            return (
                                                <td className="page-table-td" style={{ width: '12%', textAlign: 'center' }}>
                                                    <div className="flex flex-col items-center gap-1">
                                                        {/* Status badge */}
                                                        {lhdn ? (
                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${lhdn.cls}`}>
                                                                {lhdn.label}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-gray-400">—</span>
                                                        )}

                                                        {/* Consolidated: read-only, no individual actions */}
                                                        {isConsolidated && (
                                                            <span className="text-xs text-gray-400 italic">Admin submitted</span>
                                                        )}

                                                        {/* Individual e-invoice actions only */}
                                                        {!isConsolidated && doc.lhdn_status === 'valid' && doc.lhdn_long_id && doc.lhdn_document_uuid && (
                                                            <a
                                                                href={`https://myinvois.hasil.gov.my/${doc.lhdn_document_uuid}/share/${doc.lhdn_long_id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs text-blue-500 hover:underline"
                                                                title={`https://myinvois.hasil.gov.my/${doc.lhdn_document_uuid}/share/${doc.lhdn_long_id}`}
                                                            >
                                                                Validation Link
                                                            </a>
                                                        )}
                                                        {!isConsolidated && (doc.lhdn_status === 'submitted' || (doc.lhdn_status === 'valid' && !doc.lhdn_long_id)) && (
                                                            <button
                                                                onClick={() => handleCheckLhdnStatus(doc)}
                                                                className="text-xs text-blue-600 hover:underline"
                                                            >
                                                                {doc.lhdn_status === 'valid' ? 'Get Link' : 'Refresh'}
                                                            </button>
                                                        )}
                                                        {!isConsolidated && (doc.lhdn_status === null || doc.lhdn_status === 'invalid') && isSameMonth && (
                                                            <button
                                                                onClick={() => handleApplyEInvoice(doc)}
                                                                className="text-xs text-indigo-600 hover:underline"
                                                            >
                                                                {doc.lhdn_status === 'invalid' ? 'Retry' : 'Request'}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })()}
                                        <td className="page-table-td" style={{ width: '8%', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleDownloadInvoice(doc)}
                                                className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-200 transition flex items-center space-x-1 mx-auto"
                                                title="Download Invoice"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                <span>Download</span>
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!salesLoading && filteredDocuments.length > 0 && (
                    <div className="flex items-center justify-between text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                            <span>Rows per page:</span>
                            <select 
                                value={rowsPerPage} 
                                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                                className="border border-gray-300 rounded px-2 py-1"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                        <span>
                            {startIndex + 1}-{Math.min(endIndex, filteredDocuments.length)} of {filteredDocuments.length}
                        </span>
                        <div className="flex space-x-1">
                            <button
                                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-gray-100"
                            >
                                &lt;
                            </button>
                            <button
                                className="px-2 py-1 border rounded bg-blue-500 text-white"
                            >
                                {currentPage}
                            </button>
                            <button
                                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-2 py-1 border rounded disabled:opacity-40 hover:bg-gray-100"
                            >
                                &gt;
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <EInvoiceDetailsModal
                open={showEInvoiceModal}
                orgId={pendingEInvoiceDoc?.org_id ?? selectedCompany?.company_id}
                orgName={selectedCompany?.company_name}
                orgCountryIso={orgCountryIso}
                title={pendingEInvoiceDoc ? "Apply e-Invoice – Buyer details" : "E-Invoice Details"}
                submitLabel={pendingEInvoiceDoc ? "Save & Apply" : "Save Details"}
                onClose={closeEInvoiceModal}
                onSaved={handleEInvoiceSaved}
                onRemoved={() => { setHasEInvoiceDetails(false); }}
            />
        </div>
    );
}